<script setup lang="ts">
import { loyaltyHubCashbackArt } from '@/utils/loyalty-hub';

// Card de cashback do hub — composto em DOM, não uma imagem exportada. Mesmo
// tratamento que o RouletteVipCard: gradiente, texto e CTA em CSS, e só o herói
// (o baú) é imagem.
//
// Por que DOM e não imagem: eram dois webps pré-compostos (mobile + desktop,
// ~172 KB somados) com o texto assado dentro — sem acessibilidade e sem poder
// mudar copy sem re-export. Agora a copy vem do contrato e a arte é UM arquivo
// pros dois breakpoints (o baú é o mesmo desenho; muda só o enquadramento).
//
// Medidas: Figma node 21280:20173 (mobile, card 374 × 88) e 21571:46435
// (desktop, 551,5 × 164). Como no card da roletinha, os dois desenhos NÃO são um
// do outro em escala — o desktop usa outro ângulo de gradiente e um recorte mais
// estreito do baú —, então cada breakpoint tem seu bloco de medidas. O DOM é o
// mesmo; muda só o CSS.
defineProps<{
  // Textos vêm de fora (contrato do hub) — o componente não decide copy.
  title: string;
  subtitle: string;
  cta: string;
}>();

const emit = defineEmits<{ (e: 'select'): void }>();

const chest = loyaltyHubCashbackArt();
</script>

<template>
  <button
    class="cbc"
    type="button"
    @click="emit('select')"
    @dragstart.prevent
  >
    <!-- O baú sangra acima do corpo (é assim no Figma) e precisa ser cortado
         pelo canto arredondado embaixo à direita. Quem faz esse corte é esta
         caixa, não o card: o card não pode ter `overflow: hidden`, senão come
         justamente a sangria de cima. -->
    <span class="cbc__art">
      <img
        :alt="''"
        aria-hidden="true"
        class="cbc__chest"
        decoding="async"
        loading="lazy"
        :src="chest"
      />
    </span>

    <span class="cbc__content">
      <span class="cbc__text">
        <span class="cbc__title">{{ title }}</span>
        <span class="cbc__subtitle">{{ subtitle }}</span>
      </span>

      <span class="cbc__cta">{{ cta }}</span>
    </span>
  </button>
</template>

<style scoped lang="scss">
// TODAS as medidas em `cqw` — 1cqw = 1% da LARGURA DO CARD, ou seja 3,74px no
// desenho mobile e 5,515px no desktop. Cada valor é `medida_do_figma / largura`.
// Cada declaração `cqw` vem depois de uma em `px` (o valor no desenho), que é o
// fallback pra navegador sem container query. Mesma régua do RouletteVipCard —
// os detalhes de por que `cqw` e não `%` estão comentados lá.

/* ================= MOBILE (base) — card 374 × 88 ================= */
.cbc {
  container-type: inline-size;
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  aspect-ratio: 374 / 88;
  // ATENÇÃO: nada de `cqw` NESTE seletor — um elemento não consulta a si mesmo
  // (ver a nota longa no RouletteVipCard). O recuo lateral vive no `__content`.
  padding: 0;
  // Sem borda: o Figma tem um traço de `rgba(255,255,255,.05)` em volta do card,
  // mas na tela ele só suja o contorno contra a névoa do painel — mesma decisão
  // do card da roletinha.
  border: 0;
  border-radius: 12px;
  // Ângulo diferente por breakpoint (−37,28° no mobile, −25,37° no desktop).
  // Cores: colors/slate/400 → tabs/default.
  background-image: linear-gradient(-37.28deg, #94a3b8 23.36%, #272f3f 124.48%);
  // O baú passa do topo — o card não pode cortar.
  overflow: visible;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;

  &:active {
    filter: brightness(0.94);
  }
}

// Caixa do baú: 133 de largura (35,5615%), do topo −13 (−14,7727% de 88) até a
// base do card. O canto arredondado sai daqui, e NÃO assado no `.webp`: o
// arquivo é retangular e o corte é do navegador, que é o único jeito de o raio
// acompanhar o card se ele mudar de tamanho.
.cbc__art {
  position: absolute;
  top: -14.7727%;
  right: 0;
  bottom: 0;
  width: 35.5615%;
  overflow: hidden;
  border-bottom-right-radius: 12px;
  // Decoração não participa do hit-test: sem isto a faixa que sobe além do card
  // pairaria sobre o card anterior e roubaria o clique dele (no desktop a
  // sangria é 23px contra 16px de gap do grid, então ela alcança MESMO).
  pointer-events: none;
}

// Enquadramento mobile: o baú mede 136,275 numa caixa de 133 e começa 3,77 antes
// da borda esquerda dela. Aqui ele é dimensionado pela LARGURA (e no desktop pela
// altura, ver abaixo): é o que garante que ele cubra a caixa até a borda direita —
// pela altura sobraria ~1px de gradiente aparecendo no canto.
.cbc__chest {
  display: block;
  position: absolute;
  top: 0;
  left: -2.8346%;
  width: 102.4624%;
  height: auto;
  max-width: none;
  user-select: none;
  -webkit-user-drag: none;
}

// Coluna "Text+cta": 66 de altura, texto no topo e CTA na base.
.cbc__content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  // `flex-start` e não o `stretch` padrão, pro CTA não esticar na largura toda.
  align-items: flex-start;
  justify-content: space-between;
  height: 66px;
  height: 17.6471cqw;
  // O recuo lateral do card (11 no mobile) mora aqui — ver a nota em `.cbc`.
  margin-left: 11px;
  margin-left: 2.9412cqw;
}

// Bloco de texto: 34 de altura = título (20) + 1 de gap + subtítulo (13).
.cbc__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  gap: 0.2674cqw;
}

.cbc__title {
  color: #fff;
  font-size: 14px;
  font-size: 3.7433cqw;
  font-weight: 600;
  // 20 / 14 — a altura da linha declarada no Figma (line-height/20).
  line-height: 1.4286;
  white-space: nowrap;
}

.cbc__subtitle {
  color: #fff;
  font-size: 10px;
  font-size: 2.6738cqw;
  font-weight: 400;
  // 13 / 10 — a altura da CAIXA do Figma (ele reporta `leading: normal`).
  line-height: 1.3;
  white-space: nowrap;
}

// CTA: padding 5/12/6, borda 1, radius 16, 10px/600 — as mesmas medidas do card
// da roletinha (é o mesmo componente de botão no Figma).
// É um <span> e não um <button>: o card inteiro já é o botão, e botão dentro de
// botão é HTML inválido (o navegador desmonta a árvore).
.cbc__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-width: 8.5561cqw;
  padding: 5px 12px 6px;
  padding: 1.3369cqw 3.2086cqw 1.6043cqw;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-width: 0.2674cqw;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: #fcfcfc;
  font-size: 10px;
  font-size: 2.6738cqw;
  font-weight: 600;
  // 1,15 e não 1: o Figma usa `leading: normal` no rótulo, o que dá ~24,3 de
  // altura no botão. Com `1` o botão sai 2px mais baixo que o desenho.
  line-height: 1.15;
  white-space: nowrap;
}

/* ================= DESKTOP — card 551,5 × 164 ================= */
// Aqui 1cqw = 5,515px. O breakpoint é o mesmo que o hub usa pra trocar a arte dos
// outros cards (<picture> em Card.vue).
@media (min-width: 1024px) {
  .cbc {
    aspect-ratio: 551.5 / 164;
    background-image: linear-gradient(
      -25.37deg,
      #94a3b8 23.36%,
      #272f3f 124.48%
    );
  }

  .cbc__art {
    top: -14.109%; // −23,1387 / 164
    width: 44.483%; // 245,324 / 551,5
  }

  // Enquadramento desktop: o MESMO arquivo, recorte mais estreito. Dimensionado
  // pela ALTURA (100% da caixa = 187,14, o que dá 251,4 de largura) e empurrado
  // 26,617 pra direita; o que passa da borda direita da caixa é cortado — é
  // assim que o desktop mostra menos baú que o mobile sem um segundo arquivo.
  .cbc__chest {
    left: 10.8496%; // 26,617 / 245,324
    width: auto;
    height: 100%;
  }

  // Desktop: a coluna toda é centrada na altura e o CTA fica 11,36 abaixo do
  // texto (no mobile ele é empurrado pra base por `space-between`).
  .cbc__content {
    justify-content: center;
    gap: 11.36px;
    gap: 2.0598cqw;
    height: 100%;
    margin-left: 20px;
    margin-left: 3.6265cqw;
  }

  .cbc__text {
    gap: 6px;
    gap: 1.0879cqw;
  }

  .cbc__title {
    font-size: 22.45px;
    font-size: 4.0709cqw;
    // 28,865 / 22,451 — a ALTURA DA CAIXA do Figma, não o `leading` que ele
    // reporta (32,073). Quem manda no empilhamento é a caixa.
    line-height: 1.2857;
  }

  // Aqui vale o 16,036 do Figma (o card da roletinha desceu pra 15 porque a
  // legenda dele QUEBRAVA em duas linhas na coluna de 269; esta é `nowrap`, então
  // não quebra — corre por baixo da caixa do baú, que naquele trecho é só
  // transparência, e é assim que o desenho fecha).
  // 22,14 / 16,036 é a altura da CAIXA do Figma, não o `leading` que ele reporta.
  .cbc__subtitle {
    font-size: 16.04px;
    font-size: 2.9077cqw;
    line-height: 1.3806;
  }

  .cbc__cta {
    min-width: 121.49px;
    min-width: 22.0284cqw;
    padding: 8.02px 19.24px 9.62px;
    padding: 1.4539cqw 3.4894cqw 1.7448cqw;
    border-width: 1.6px;
    border-width: 0.2909cqw;
    font-size: 16.04px;
    font-size: 2.9077cqw;
    // 16,79 / 16,036 — o que fecha os 37,64 de altura do botão no desenho
    // desktop (o mobile usa 1,15, que é a proporção do botão pequeno).
    line-height: 1.047;
  }
}
</style>
